/**
 * Full-screen signup popup (sections/klaviyo-signup-popup.liquid)
 *
 * Steps: vibe buttons -> email -> SMS (optional) -> success.
 *
 * The list's own opt-in settings decide what happens after a successful
 * subscribe: double opt-in sends a confirmation, single opt-in fires the
 * welcome flow immediately. Either way Klaviyo — not this script — delivers
 * the discount code.
 */

/**
 * Klaviyo's modern Client Subscriptions API (a.klaviyo.com/client/subscriptions/)
 * CANNOT be used from the storefront. It sits behind DataDome bot protection,
 * which answers browser-originated requests with a 403 carrying no CORS headers
 * — so the browser reports it as "blocked by CORS policy: No
 * 'Access-Control-Allow-Origin' header". The same request succeeds from curl,
 * which is why it looked fine in testing and silently dropped every real signup.
 *
 * This is Klaviyo's older AJAX endpoint. It is form-encoded, which makes it a
 * CORS-simple request: no preflight, no custom headers, no DataDome challenge.
 * It is also what the theme's existing <custom-klaviyo> footer newsletter uses
 * (global.js.liquid), so it is proven on this storefront.
 *
 * Verified: creates the profile, stores custom properties via $fields, sets
 * email marketing consent, and is not bot-suppressed.
 *
 * GOTCHA WHEN TESTING: this endpoint normalises plus-addressing, so
 * you+test@gmail.com resolves to you@gmail.com. If the base address is already
 * on the list it answers {"success": true, "is_subscribed": true} and does
 * nothing at all — no profile, no "Added to List" event, no welcome email.
 * It looks like a silent failure but is working as intended. Test with a
 * genuinely unused address, not a +tag of one that is already subscribed.
 */
const KLAVIYO_SUBSCRIBE_URL = 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe';
const MOBILE_BREAKPOINT = 768;

// ISO|Name|dial code. US and Canada are pinned first as the primary market,
// the rest are alphabetical. Add rows here if a market is missing.
const COUNTRIES = [
  'US|United States|1', 'CA|Canada|1', 'GB|United Kingdom|44', 'AU|Australia|61',
  'AF|Afghanistan|93', 'AL|Albania|355', 'DZ|Algeria|213', 'AD|Andorra|376',
  'AO|Angola|244', 'AR|Argentina|54', 'AM|Armenia|374', 'AT|Austria|43',
  'AZ|Azerbaijan|994', 'BH|Bahrain|973', 'BD|Bangladesh|880', 'BB|Barbados|1',
  'BY|Belarus|375', 'BE|Belgium|32', 'BZ|Belize|501', 'BM|Bermuda|1',
  'BO|Bolivia|591', 'BA|Bosnia and Herzegovina|387', 'BR|Brazil|55',
  'BG|Bulgaria|359', 'KH|Cambodia|855', 'CM|Cameroon|237', 'CL|Chile|56',
  'CN|China|86', 'CO|Colombia|57', 'CR|Costa Rica|506', 'HR|Croatia|385',
  'CY|Cyprus|357', 'CZ|Czechia|420', 'DK|Denmark|45', 'DO|Dominican Republic|1',
  'EC|Ecuador|593', 'EG|Egypt|20', 'SV|El Salvador|503', 'EE|Estonia|372',
  'ET|Ethiopia|251', 'FI|Finland|358', 'FR|France|33', 'GE|Georgia|995',
  'DE|Germany|49', 'GH|Ghana|233', 'GR|Greece|30', 'GT|Guatemala|502',
  'HN|Honduras|504', 'HK|Hong Kong|852', 'HU|Hungary|36', 'IS|Iceland|354',
  'IN|India|91', 'ID|Indonesia|62', 'IE|Ireland|353', 'IL|Israel|972',
  'IT|Italy|39', 'JM|Jamaica|1', 'JP|Japan|81', 'JO|Jordan|962',
  'KZ|Kazakhstan|7', 'KE|Kenya|254', 'KW|Kuwait|965', 'LV|Latvia|371',
  'LB|Lebanon|961', 'LT|Lithuania|370', 'LU|Luxembourg|352', 'MY|Malaysia|60',
  'MT|Malta|356', 'MX|Mexico|52', 'MD|Moldova|373', 'MC|Monaco|377',
  'MA|Morocco|212', 'NP|Nepal|977', 'NL|Netherlands|31', 'NZ|New Zealand|64',
  'NI|Nicaragua|505', 'NG|Nigeria|234', 'MK|North Macedonia|389', 'NO|Norway|47',
  'OM|Oman|968', 'PK|Pakistan|92', 'PA|Panama|507', 'PY|Paraguay|595',
  'PE|Peru|51', 'PH|Philippines|63', 'PL|Poland|48', 'PT|Portugal|351',
  'PR|Puerto Rico|1', 'QA|Qatar|974', 'RO|Romania|40', 'RS|Serbia|381',
  'SG|Singapore|65', 'SK|Slovakia|421', 'SI|Slovenia|386', 'ZA|South Africa|27',
  'KR|South Korea|82', 'ES|Spain|34', 'LK|Sri Lanka|94', 'SE|Sweden|46',
  'CH|Switzerland|41', 'TW|Taiwan|886', 'TZ|Tanzania|255', 'TH|Thailand|66',
  'TT|Trinidad and Tobago|1', 'TN|Tunisia|216', 'TR|Turkiye|90', 'UG|Uganda|256',
  'UA|Ukraine|380', 'AE|United Arab Emirates|971', 'UY|Uruguay|598',
  'VE|Venezuela|58', 'VN|Vietnam|84'
];

class KlaviyoPopup extends HTMLElement {
  constructor() {
    super();

    this.dialog = this.querySelector('[data-kpopup-dialog]');
    this.steps = Array.from(this.querySelectorAll('[data-kpopup-step]'));

    this.config = {
      companyId: this.dataset.companyId,
      defaultList: this.dataset.defaultList,
      propertyKey: this.dataset.propertyKey || 'jewelry_vibe',
      sourceName: this.dataset.sourceName || 'Signup Popup',
      smsEnabled: this.dataset.smsEnabled === 'true',
      smsList: this.dataset.smsList,
      defaultCountry: (this.dataset.defaultCountry || 'US').toUpperCase(),
      smsCountries: (this.dataset.smsCountries || '')
        .toUpperCase()
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean),
      delay: parseInt(this.dataset.delay, 10),
      scrollEnabled: this.dataset.scrollEnabled === 'true',
      scrollPercent: parseInt(this.dataset.scrollPercent, 10) || 30,
      exitIntent: this.dataset.exitIntent === 'true',
      showDesktop: this.dataset.showDesktop === 'true',
      showMobile: this.dataset.showMobile === 'true',
      hideDays: parseInt(this.dataset.hideDays, 10),
      suppressLoggedIn: this.dataset.suppressLoggedIn === 'true',
      isCustomer: this.dataset.isCustomer === 'true',
      designMode: this.dataset.designMode === 'true',
      forceShow: this.dataset.forceShow === 'true',
      storageKey: this.dataset.storageKey || 'ep_signup_popup'
    };

    // Collected across steps, submitted to Klaviyo per step.
    this.selection = { vibe: null, listId: null, email: null, submittedSms: false };

    this.isOpen = false;
    this.timerId = null;
    this.onScroll = this.onScroll.bind(this);
    this.onExitIntent = this.onExitIntent.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    if (!this.dialog || !this.config.companyId) return;

    this.buildCountrySelect();
    this.bindEvents();

    // In the theme editor the merchant needs to see it immediately, and
    // suppression would make the section look broken. The "Always show"
    // testing setting does the same thing on the live storefront.
    if (this.config.designMode || this.config.forceShow) {
      this.open();
      return;
    }

    if (this.shouldSuppress()) return;

    this.armTriggers();
  }

  disconnectedCallback() {
    this.disarmTriggers();
    document.removeEventListener('keydown', this.onKeydown);
  }

  /* ------------------------------------------------------------------ *
   * Suppression
   * ------------------------------------------------------------------ */

  shouldSuppress() {
    if (this.config.suppressLoggedIn && this.config.isCustomer) return true;
    if (!this.matchesViewport()) return true;
    if (this.isDismissed()) return true;

    return false;
  }

  matchesViewport() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    return isMobile ? this.config.showMobile : this.config.showDesktop;
  }

  /**
   * "Hide for 0 days" means never again, which a cookie can't express, so that
   * case falls back to localStorage.
   */
  isDismissed() {
    try {
      if (window.localStorage.getItem(`${this.config.storageKey}_never`) === 'true') {
        return true;
      }
    } catch (e) {
      // Private mode or blocked storage — fall through to the cookie.
    }

    return this.getCookie(this.config.storageKey) === 'dismissed';
  }

  markDismissed() {
    if (this.config.hideDays === 0) {
      try {
        window.localStorage.setItem(`${this.config.storageKey}_never`, 'true');
        return;
      } catch (e) {
        // Storage unavailable — fall back to a long-lived cookie.
      }
    }

    const days = this.config.hideDays === 0 ? 365 : this.config.hideDays;
    const expires = new Date();
    expires.setDate(expires.getDate() + days);

    // Set directly rather than via Utility.setCookie, which omits path=/ and
    // would scope the cookie to whatever page the shopper closed it on.
    document.cookie = `${this.config.storageKey}=dismissed;expires=${expires.toUTCString()};path=/`;
  }

  getCookie(name) {
    const match = document.cookie.match(new RegExp(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`));

    return match ? match.pop() : '';
  }

  /* ------------------------------------------------------------------ *
   * Triggers — first one to fire wins, the rest are torn down
   * ------------------------------------------------------------------ */

  armTriggers() {
    if (!isNaN(this.config.delay)) {
      this.timerId = setTimeout(() => this.open(), this.config.delay * 1000);
    }

    if (this.config.scrollEnabled) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    // Exit intent needs a cursor, so it is desktop-only by nature.
    if (this.config.exitIntent && window.innerWidth >= MOBILE_BREAKPOINT) {
      document.addEventListener('mouseout', this.onExitIntent);
    }
  }

  disarmTriggers() {
    if (this.timerId) clearTimeout(this.timerId);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('mouseout', this.onExitIntent);
  }

  onScroll() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;

    const percent = (window.scrollY / scrollable) * 100;
    if (percent >= this.config.scrollPercent) this.open();
  }

  onExitIntent(event) {
    // Only when the cursor actually leaves the top of the window.
    if (event.clientY > 0 || event.relatedTarget) return;

    this.open();
  }

  /* ------------------------------------------------------------------ *
   * Open / close
   * ------------------------------------------------------------------ */

  open() {
    if (this.isOpen) return;

    this.disarmTriggers();
    this.isOpen = true;
    this.previouslyFocused = document.activeElement;

    this.removeAttribute('hidden');
    document.body.classList.add('kpopup-open');
    document.addEventListener('keydown', this.onKeydown);

    if (typeof Utility !== 'undefined' && Utility.trapFocus) {
      Utility.trapFocus(this.dialog);
    }

    // Focus the dialog container rather than the heading. Because the popup
    // appears on a timer rather than in response to anything the shopper did,
    // landing on the container makes the screen reader announce the role, the
    // name and the "press Escape to close" hint — so they are told a promotion
    // opened instead of just being moved mid-page. Step changes still focus
    // their own heading, where the context is already established.
    this.dialog.focus();

    this.dispatchEvent(new CustomEvent('kpopup:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.setAttribute('hidden', '');
    document.body.classList.remove('kpopup-open');
    document.removeEventListener('keydown', this.onKeydown);

    if (typeof Utility !== 'undefined' && Utility.removeTrapFocus) {
      Utility.removeTrapFocus(this.previouslyFocused);
    } else if (this.previouslyFocused && this.previouslyFocused.focus) {
      this.previouslyFocused.focus();
    }

    // Closing while testing must not write the cookie, or the next reload
    // would be suppressed and "always show" would look broken.
    if (!this.config.designMode && !this.config.forceShow) this.markDismissed();

    this.dispatchEvent(new CustomEvent('kpopup:close', { bubbles: true }));
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  /* ------------------------------------------------------------------ *
   * Steps
   * ------------------------------------------------------------------ */

  currentStep() {
    return this.steps.find((step) => !step.hasAttribute('hidden'));
  }

  goToStep(name) {
    const target = this.steps.find((step) => step.dataset.kpopupStep === name);
    if (!target) return;

    this.steps.forEach((step) => step.toggleAttribute('hidden', step !== target));

    // Keep the dialog's accessible name pointing at the visible heading.
    const heading = target.querySelector('.kpopup__heading');
    if (heading && heading.id) this.dialog.setAttribute('aria-labelledby', heading.id);

    this.focusStepHeading(target);
  }

  focusStepHeading(step) {
    if (!step) return;

    const heading = step.querySelector('.kpopup__heading');
    const focusTarget = heading || step.querySelector('button, input, a');
    if (focusTarget && focusTarget.focus) focusTarget.focus();
  }

  /* ------------------------------------------------------------------ *
   * Events
   * ------------------------------------------------------------------ */

  bindEvents() {
    this.querySelectorAll('[data-kpopup-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    const backdrop = this.querySelector('[data-kpopup-backdrop]');
    if (backdrop) backdrop.addEventListener('click', () => this.close());

    this.querySelectorAll('[data-kpopup-vibe]').forEach((button) => {
      button.addEventListener('click', () => {
        this.selection.vibe = button.dataset.vibe || button.textContent.trim();
        this.selection.listId = button.dataset.listId || this.config.defaultList;
        this.goToStep('email');
      });
    });

    const emailForm = this.querySelector('[data-kpopup-form="email"]');
    if (emailForm) emailForm.addEventListener('submit', (e) => this.onEmailSubmit(e));

    const smsForm = this.querySelector('[data-kpopup-form="sms"]');
    if (smsForm) smsForm.addEventListener('submit', (e) => this.onSmsSubmit(e));

    const skipSms = this.querySelector('[data-kpopup-skip-sms]');
    if (skipSms) skipSms.addEventListener('click', () => this.showSuccess(false));
  }

  async onEmailSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const input = form.querySelector('[data-kpopup-email]');
    const email = input.value.trim();

    if (!this.isValidEmail(email)) {
      this.showError(form, 'Please enter a valid email address.', input);
      return;
    }

    this.clearError(form, input);
    this.selection.email = email;

    const ok = await this.subscribe(form, {
      listId: this.selection.listId || this.config.defaultList,
      email: email,
      properties: this.vibeProperties()
    });

    if (!ok) return;

    this.identify(email);

    if (this.config.smsEnabled && this.querySelector('[data-kpopup-step="sms"]')) {
      this.goToStep('sms');
    } else {
      this.showSuccess(false);
    }
  }

  async onSmsSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const input = form.querySelector('[data-kpopup-phone]');
    const phone = this.toE164(input.value);

    if (!phone) {
      this.showError(form, 'Please enter a valid phone number.', input);
      return;
    }

    this.clearError(form, input);

    const ok = await this.subscribe(form, {
      listId: this.config.smsList,
      // Sent with the email so Klaviyo matches the existing profile rather
      // than creating a second one keyed only to the phone number.
      email: this.selection.email,
      phone: phone,
      properties: this.vibeProperties()
    });

    if (!ok) return;

    this.showSuccess(true);
  }

  showSuccess(viaSms) {
    this.selection.submittedSms = viaSms;

    const emailCopy = this.querySelector('[data-kpopup-success-email]');
    const smsCopy = this.querySelector('[data-kpopup-success-sms]');

    if (smsCopy) {
      smsCopy.toggleAttribute('hidden', !viaSms);
      if (emailCopy) emailCopy.toggleAttribute('hidden', viaSms);
    }

    this.goToStep('success');
  }

  vibeProperties() {
    if (!this.selection.vibe) return {};

    return { [this.config.propertyKey]: this.selection.vibe };
  }

  /* ------------------------------------------------------------------ *
   * Klaviyo
   * ------------------------------------------------------------------ */

  async subscribe(form, { listId, email, phone, properties }) {
    if (!listId) {
      this.showError(form, 'This form is not configured correctly. Please try again later.');
      return false;
    }

    const button = form.querySelector('[data-kpopup-submit]');
    this.setLoading(button, true);

    // Form-encoded with no custom headers, so the browser treats this as a
    // CORS-simple request and skips the preflight. Do not add headers here.
    const params = new URLSearchParams();
    params.set('a', this.config.companyId);
    params.set('g', listId);
    params.set('email', email);

    if (phone) {
      params.set('phone_number', phone);
      params.set('sms_consent', 'true');
    }

    // Custom profile properties have to be declared in $fields to be stored.
    const fields = [];

    Object.keys(properties || {}).forEach((key) => {
      fields.push(key);
      params.set(key, properties[key]);
    });

    if (this.config.sourceName) {
      fields.push('$source');
      params.set('$source', this.config.sourceName);
    }

    if (fields.length) params.set('$fields', fields.join(','));

    try {
      const response = await fetch(KLAVIYO_SUBSCRIBE_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: params.toString()
      });

      const body = await response.json();

      if (body && body.success) return true;

      this.showError(form, this.friendlyError(body));
      return false;
    } catch (error) {
      console.error('Klaviyo popup subscribe failed', error);
      this.showError(form, 'Something went wrong. Please try again.');
      return false;
    } finally {
      this.setLoading(button, false);
    }
  }

  /**
   * The endpoint returns { success: false, errors: ["..."] }. Those strings are
   * written for developers ("List does not exist."), so only surface the ones a
   * shopper can act on. Everything else gets a generic message, with the real
   * text logged to the console for whoever is debugging.
   */
  friendlyError(body) {
    const fallback = 'Sorry, we could not sign you up. Please try again.';
    const first = (body && body.errors && body.errors[0]) || '';
    // The endpoint returns plain strings; tolerate an object shape too, since
    // this format is not formally documented.
    const detail = typeof first === 'string' ? first : first.detail || '';

    console.error('Klaviyo popup subscribe rejected:', detail || body);

    const actionable = /phone|email/i.test(detail) && !/list does not exist/i.test(detail);

    return actionable ? detail : fallback;
  }

  /**
   * Links the browser session to the profile so Klaviyo's onsite tracking and
   * its own "existing user" suppression see the same person.
   */
  identify(email) {
    try {
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify({ $email: email });
      }
    } catch (e) {
      // Onsite script absent or blocked — non-fatal.
    }
  }

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  /**
   * A native <select> rather than a custom listbox: it gets keyboard type-ahead
   * and the OS picker on mobile for free, and avoids the ARIA problems a
   * hand-rolled combobox invites.
   */
  buildCountrySelect() {
    const select = this.querySelector('[data-kpopup-country]');
    if (!select || select.options.length) return;

    const fragment = document.createDocumentFragment();

    // Only offer countries Klaviyo can actually text. Collecting a number from
    // a country with no sender ID looks like it worked and then silently never
    // sends — which is exactly how this went unnoticed.
    const allowed = this.config.smsCountries;
    const rows = allowed.length
      ? COUNTRIES.filter((row) => allowed.includes(row.split('|')[0]))
      : COUNTRIES;

    rows.forEach((row) => {
      const [iso, name, dial] = row.split('|');
      const option = document.createElement('option');

      option.value = dial;
      option.dataset.iso = iso;
      // Name first so the browser's built-in type-ahead works — typing "slov"
      // jumps to Slovenia. A leading flag emoji would break that, and Windows
      // renders flags as bare letters anyway.
      option.textContent = `${name} +${dial}`;
      if (iso === this.config.defaultCountry) option.selected = true;

      fragment.appendChild(option);
    });

    select.appendChild(fragment);
  }

  selectedDialCode() {
    const select = this.querySelector('[data-kpopup-country]');

    return (select && select.value) || '1';
  }

  /**
   * Klaviyo requires E.164. Accepts what shoppers actually type — spaces,
   * dashes, parentheses, a leading 00 or +.
   *
   * The dial code comes from the country picker, so prepending it is a fact
   * rather than a guess. Without that, a local foreign number silently becomes
   * a plausible-looking wrong one — 030 785 456 would be stored as
   * +1030785456, a number that can never receive a message.
   */
  toE164(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const allDigits = raw.replace(/\D/g, '');

    // An explicit international prefix overrides the picker.
    if (raw.startsWith('+')) return this.asE164(allDigits);
    if (allDigits.startsWith('00')) return this.asE164(allDigits.slice(2));

    const dial = this.selectedDialCode();
    // Strip the national trunk prefix before adding the country code.
    const digits = allDigits.replace(/^0+/, '');

    if (dial === '1') {
      // NANP numbers are exactly 10 digits, or 11 beginning with 1.
      if (digits.length === 11 && digits.startsWith('1')) return this.asE164(digits);

      return digits.length === 10 ? this.asE164('1' + digits) : null;
    }

    return this.asE164(digits.startsWith(dial) ? digits : dial + digits);
  }

  asE164(digits) {
    // E.164 allows up to 15 digits; below 8 is too short to be a real number.
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }

  setLoading(button, isLoading) {
    if (!button) return;

    button.classList.toggle('is-loading', isLoading);
    button.disabled = isLoading;
  }

  showError(form, message, input) {
    const region = form.querySelector('[data-kpopup-error]');
    if (region) region.textContent = message;

    if (input) {
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    }
  }

  clearError(form, input) {
    const region = form.querySelector('[data-kpopup-error]');
    if (region) region.textContent = '';

    if (input) input.removeAttribute('aria-invalid');
  }
}

customElements.define('klaviyo-popup', KlaviyoPopup);
